import { useEffect, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabase";
import { GOOGLE_MAPS_KEY, geocodeAddress } from "../lib/maps";
import { loadStripe } from "../lib/stripe";
import { formatPhoneInput } from "../lib/format";
// DEO<=CLEANUP rule — single source of truth lives in src/lib/pricing.js (plain
// exported const, import-safe for this standalone public page). Deo cadence may
// never be more frequent than the chosen cleanup cadence.
import { DEO_FREQ_OPTIONS, DEO_VISITS_PER_MONTH } from "../lib/pricing";
import { resolveAttribution, buildThankYouUrl, parseAllowedOrigins, planSlug,
         normalizeEmail, normalizePhoneE164, sha256Hex,
         msgResize, msgStep, msgOutOfArea, msgSignupComplete, msgSignupPending,
         msgAnalyticsStep, msgAction } from "../lib/tracking";

// ════════════════════════════════════════════════════════════════════
//  SignupPage — PUBLIC, standalone client-onboarding page for an operator.
//  Rendered (by index.js) for the path /signup/<org-slug>, OUTSIDE the
//  authed CRM (no login). The operator links a "Sign Up" button to it OR
//  drops it in an <iframe>. It pulls the operator's public config by slug,
//  collects service details + address (Places autocomplete → lat/lng), and
//  posts to public-onboard-client.
//
//  REDESIGN: a momentum-building 3-step flow whose centerpiece is an
//  OPTIONAL Stripe card step, psychologically promoted as the default
//  ("add card now → instant activation"). Card is tokenized on the
//  operator's CONNECTED account via loadStripe(cfg.stripe_account_id),
//  mirroring ClientModal.js. If the customer adds a card we POST an extra
//  payment_method_id (+ pm_last4, pm_brand) so the backend activates them
//  immediately; if they skip, the existing payment_pending email-link path
//  runs unchanged. All original wiring (config fetch, Places autocomplete,
//  service-area validation, geocode fallback, POST) is preserved.
// ════════════════════════════════════════════════════════════════════

const FREQ_LABELS = { weekly: "Weekly", biweekly: "Bi-weekly", "twice-weekly": "Twice a week", monthly: "Monthly" };
// Deo cadences are presented most-frequent → least-frequent so the clamp can
// fall back to the "most-frequent still-allowed" option by taking the first one.
const DEO_FREQ_ORDER = ["weekly", "biweekly", "monthly"];
const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);

// DISPLAY-ONLY per-visit derivation (marketing handoff v5): every ad surface anchors
// on "$X / visit", so the bold number in the form must be per-visit too — the stored
// monthly rate stays exactly what Stripe bills. Divisors are exact (52 weeks / 12
// months, not the 4.33 billing shorthand) so the division lands back on the clean
// per-visit cents the rate card was built from.
const CLEANUP_VISITS_PER_MONTH = { weekly: 52 / 12, biweekly: 26 / 12, "twice-weekly": 104 / 12 };

// Bold per-visit first; the monthly amount stays visible (customers are billed
// monthly and must not be surprised at checkout) but demoted to small grey
// parentheses. Falls back to the old monthly-only rendering when a per-visit
// price can't be derived, rather than showing nothing.
const PerVisitPrice = ({ pv, monthly, plus = false }) => {
  const sign = plus ? "+" : "";
  if (pv == null) return <span style={{ fontWeight: 700 }}>{sign}{money(monthly)}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>/mo</span></span>;
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span style={{ fontWeight: 700 }}>{sign}{money(pv)}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>/visit</span></span>
      {/* inherit + opacity, not a fixed grey: this renders inside pills whose
          selected state flips the text white, where #64748b would vanish. */}
      {monthly != null && <span style={{ fontSize: 11, fontWeight: 400, color: "inherit", opacity: 0.72, marginLeft: 6 }}>({sign}{money(monthly)}/mo)</span>}
    </span>
  );
};
// Local YYYY-MM-DD (matches ClientModal's new Date().toISOString().split("T")[0]).
const todayStr = () => new Date().toISOString().split("T")[0];
// Backend caps the start date at ~90 days out (public-onboard-client/index.ts).
const maxStartStr = () => new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Cloudflare Turnstile site key — env-driven; undefined is fine (widget skipped).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

// ── Embed mode ──────────────────────────────────────────────────────
// When this page is iframed into an operator's marketing website (?embed=1), render
// flush + transparent + themed to their accent (?accent=#hex), and post our height so the
// host iframe can auto-resize. Everything is gated on the param, so the standalone
// /signup/<slug> page is completely unchanged.
const EMBED_PARAMS = (() => { try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); } })();
const IS_EMBED = EMBED_PARAMS.get("embed") === "1";
const EMBED_ACCENT = (() => {
  const r = (EMBED_PARAMS.get("accent") || "").trim();
  const h = r.startsWith("#") ? r : (r ? "#" + r : "");
  return /^#[0-9a-fA-F]{6}$/.test(h) ? h : "";
})();
const SEL_STYLE = EMBED_ACCENT ? { background: EMBED_ACCENT, borderColor: EMBED_ACCENT, color: "#fff" } : null;

// Framed ≠ embed param: the ads handoff wants events whenever we're actually inside
// an iframe, even if the embedder forgot ?embed=1 (visual chrome stays param-gated).
const IS_FRAMED = (() => { try { return window.self !== window.top; } catch { return true; } })();

// When the operator's own embed snippet hosts us, THEIR page owns the ad tags: it
// listens for our messages and fires the Ads conversion, GA4 purchase and Meta
// Subscribe itself. Firing them in here as well is not harmless —
//   • Meta explicitly does NOT deduplicate two BROWSER events sharing an eventID
//     ("we do not discard either"), so every embedded signup counts twice at twice
//     the value;
//   • GA4 dedupes purchase by transaction_id only within one user, and this frame is
//     a different client_id on a different hostname, which is the exact "same
//     transaction ID across different users" case Google warns undercounts key events;
//   • the parent is also the better place to fire: it is first-party to the domain
//     the ad click landed on, while this frame is third-party and gets no cookies at
//     all in Safari.
// So when embed=1 says the snippet is present, we stay silent and let it fire.
// A bare iframe WITHOUT embed=1 has no listener to rely on, so we keep firing there
// rather than leaving that operator with no conversion at all.
const PARENT_OWNS_TRACKING = IS_FRAMED && IS_EMBED;

// One conversion id per signup SESSION (not per pageview): the same uuid goes to the
// parent page, the pixels (eventID), the redirect URL, and the server webhook, so
// Meta/Google can dedup browser vs server-side reports of the same signup.
const EVENT_ID = (() => {
  try { return crypto.randomUUID(); }
  catch { return `ev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
})();

// Ad attribution present on the landing URL / cookies at first paint. Captured once —
// SPA navigation can rewrite the URL later.
const ATTRIBUTION = resolveAttribution(
  (() => { try { return window.sessionStorage; } catch { return null; } })(),
  (() => { try { return window.location.search; } catch { return ""; } })(),
  (() => { try { return document.cookie; } catch { return ""; } })(),
  (() => { try { return window.location.href.split("#")[0]; } catch { return ""; } })(),
);

export function SignupPage({ slug }) {
  const [cfg, setCfg]         = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm]       = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "",
    frequency: "", dogs: 1, deo_enabled: false, deo_frequency: "biweekly",
    start_date: todayStr(),
    notes: "", lat: null, lng: null, sms_consent: false,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [addressConfirmed, setAddressConfirmed] = useState(false); // green tick after a Places pick
  const [addressManual, setAddressManual] = useState(false); // graceful fallback: no Places suggestions returned
  const acSession  = useRef(null);
  const justPicked = useRef(false);
  const hadSuggestions = useRef(false); // did the latest query return ANY suggestions?
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]   = useState(false);
  const [activatedNow, setActivatedNow] = useState(false); // success-screen path
  const [err, setErr]     = useState("");

  // ── Flow state ────────────────────────────────────────────────────
  const [step, setStep]       = useState(1);   // 1=area, 2=plan, 3=info+card
  // Step 2 hides ALL prices until the customer picks frequency + dogs and taps
  // "See my price". Gating the price behind that selection is deliberate: it
  // turns the price into the reward for telling us what they want, so even a
  // bailer leaves behind dogs+frequency+deo (saved as a lead at the reveal).
  const [priceRevealed, setPriceRevealed] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [touched, setTouched] = useState({});  // per-field blur tracking
  const [addrHint, setAddrHint] = useState(""); // inline gate hint on step 1
  const [waitlist, setWaitlist] = useState(""); // out-of-area email capture
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistErr, setWaitlistErr] = useState("");

  // ── Lead capture (non-blocking) ───────────────────────────────────
  // The in-area lead is saved at the transition into the plan/price step.
  // leadSaved guards against re-firing on every step bounce; the backend
  // dedups so an occasional re-save (after edits) is harmless.
  const [leadId, setLeadId] = useState(null);
  const leadSaved = useRef(false);

  // ── Card (Stripe) state — mirrors ClientModal.js ──────────────────
  const [cardChoice, setCardChoice]   = useState("now"); // 'now' (default) | 'later'
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError]     = useState("");
  const [stripeReady, setStripeReady] = useState(false);
  const stripeRef   = useRef(null);
  const elementsRef = useRef(null);
  const cardElRef   = useRef(null);
  const mountedRef  = useRef(false);

  // ── Cloudflare Turnstile (bot protection) — only active if a site key is set ──
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileMountRef = useRef(null);   // the div Turnstile renders into
  const turnstileWidgetId = useRef(null);   // explicit-render widget id

  // ── Load operator public config (UNCHANGED wiring) ────────────────
  useEffect(() => {
    fetch(`${SUPABASE_URL}/functions/v1/public-operator-config?slug=${encodeURIComponent(slug)}`, { headers: { apikey: SUPABASE_ANON_KEY } })
      .then(r => r.json())
      .then(d => { if (d.error) setLoadErr(d.error); else { setCfg(d); setForm(f => ({ ...f, frequency: defaultFreq(d) })); } })
      .catch(() => setLoadErr("Couldn't load this signup page. Please try again later."));
  }, [slug]);

  function defaultFreq(c) {
    const freqs = [...new Set((c.prices || []).filter(p => p.service === "cleanup").map(p => p.frequency))];
    return freqs.includes("weekly") ? "weekly" : (freqs[0] || "weekly");
  }
  const serviceDay   = (zip) => cfg?.service_area?.find(z => z.zip === zip)?.day || null;
  // Twice-weekly needs a SECOND service day mapped for the customer's zip —
  // create-customer rejects the signup outright without one — so never offer a
  // cadence we can't actually schedule. Most zips don't have a second day set,
  // and offering it there dead-ends the customer at the moment they pay.
  const secondDay    = (zip) => cfg?.service_area?.find(z => z.zip === zip)?.second_day || null;
  const freqOffered  = (fr) => fr !== "twice-weekly" || !!secondDay(form.zip);
  const cleanupFreqs = (cfg ? [...new Set((cfg.prices || []).filter(p => p.service === "cleanup").map(p => p.frequency))] : []).filter(freqOffered);
  const priceFor     = (freq, dogs) => cfg?.prices?.find(p => p.service === "cleanup" && p.frequency === freq && Number(p.dogs) === Math.min(Number(dogs) || 1, 4))?.price;
  // Per-cadence deo PER-VISIT price from the operator rate card (service === 'deo'),
  // matching how cleanup prices are read. Null when this cadence isn't priced.
  const deoPriceFor  = (freq) => cfg?.prices?.find(p => p.service === "deo" && p.frequency === freq)?.price ?? null;
  // Deodorizer is billed MONTHLY = per-visit rate × visits/month for that cadence —
  // identical to create-customer's deoMonthly, so the price shown matches the charge.
  const deoMonthlyFor = (freq) => {
    const pv = deoPriceFor(freq);
    return pv == null ? null : Math.round(pv * (DEO_VISITS_PER_MONTH[freq] || 1) * 100) / 100;
  };
  // Monthly add-on delta for the currently-selected deo cadence (null if unpriced).
  const deoDelta     = deoMonthlyFor(form.deo_frequency);
  // Display-only per-visit for a cleanup plan (see CLEANUP_VISITS_PER_MONTH).
  const perVisitFor  = (freq, dogs) => {
    const m = priceFor(freq, dogs), d = CLEANUP_VISITS_PER_MONTH[freq];
    return m == null || !d ? null : Math.round((Number(m) / d) * 100) / 100;
  };
  // DEO<=CLEANUP: deo cadences allowed for the chosen cleanup cadence, ordered
  // most-frequent → least-frequent. Falls back to all three if cleanup unset.
  const allowedDeoFreqs = (DEO_FREQ_OPTIONS[form.frequency] || DEO_FREQ_ORDER)
    .slice()
    .sort((a, b) => DEO_FREQ_ORDER.indexOf(a) - DEO_FREQ_ORDER.indexOf(b));

  // Stripe is only usable when the platform owner has wired a connected account.
  const canShowCard  = !!cfg?.stripe_account_id;
  const wantCard     = canShowCard && cardChoice === "now";

  // ── Inject one-time <style> for :hover/:focus/keyframes ───────────
  useEffect(() => {
    if (document.getElementById("scoop-signup-css")) return;
    const el = document.createElement("style");
    el.id = "scoop-signup-css";
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);

  // ── Conversion events to the embedding page ───────────────────────
  // Posts to the operator's configured embed origin(s); "*" only as fallback —
  // acceptable because these payloads never carry PII (enforced in lib/tracking).
  const emitToParent = (payload) => {
    if (!IS_FRAMED) return;
    const origins = parseAllowedOrigins(cfg?.tracking?.embed_allowed_origins);
    try {
      if (origins.length) origins.forEach((o) => window.parent.postMessage(payload, o));
      else window.parent.postMessage(payload, "*");
    } catch { /* sandboxed parent — nothing to do */ }
  };
  const emitRef = useRef(emitToParent); emitRef.current = emitToParent;
  // GA4 narration (handoff v5). Values are option labels / field names — never PII.
  const track = (action, value) => emitRef.current(msgAction(action, value));

  // ── Ad pixels (per-business, from Settings → Tracking) ────────────
  // Injected only when the operator configured ids. Fired on the signup DOMAIN so
  // full-page visits (the SMS bot's link) convert too, not just the embedded form.
  useEffect(() => {
    const t = cfg?.tracking;
    // One switch disables every ad-platform call in this file: they are all
    // `window.fbq?.(...)` / `window.gtag?.(...)`, so with nothing injected they
    // no-op. That also stops the duplicate PageView / begin_checkout /
    // InitiateCheckout the parent already fires on our postMessage.
    if (PARENT_OWNS_TRACKING) return;
    if (t?.meta_pixel_id && !window.fbq) {
      /* eslint-disable */
      (function (f, b, e, v, n, ts, s) { if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = []; ts = b.createElement(e); ts.async = !0; ts.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(ts, s); })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      window.fbq("init", t.meta_pixel_id);
      window.fbq("track", "PageView");
    }
    // One gtag.js load serves all three Google properties (site tag, Ads conversion
    // account, GA4 stream); each still needs its own config() call or events sent to
    // that id are silently dropped.
    const googleIds = [t?.google_tag_id, t?.google_ads_conversion_id, t?.ga4_measurement_id]
      .filter(Boolean).filter((id, i, a) => a.indexOf(id) === i);
    if (googleIds.length && !window.gtag) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleIds[0])}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      googleIds.forEach((id) => window.gtag("config", id));
    }
  }, [cfg]);

  // Coverage-check failure — the ads side suppresses/retargets these.
  const oozRef = useRef("");
  useEffect(() => {
    if (valid.zip && !serviceDay(form.zip) && oozRef.current !== form.zip) {
      oozRef.current = form.zip; // once per distinct zip, not per keystroke
      emitRef.current(msgOutOfArea(form.zip));
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Funnel step events ────────────────────────────────────────────
  const step2SeenRef = useRef(false);
  const paymentStartedRef = useRef(false);
  // Latest form values for handlers that outlive a render (the Stripe card's
  // focus listener is bound once at mount — closing over `form` directly would
  // send a phone number edited after mount as its stale pre-edit value).
  const formRef = useRef(form); formRef.current = form;
  useEffect(() => {
    // ⚠️ DO NOT REMOVE msgStep: the WordPress parent fires its Google Ads
    // begin_checkout AND Meta InitiateCheckout off this legacy {type:"step"}
    // message, and the in-iframe fallbacks are suppressed under embed — removing
    // it silences both conversions with no error anywhere. Any cleanup needs a
    // coordinated parent-bridge release first (marketing session, handoff v6).
    emitRef.current(msgStep(step));
    emitRef.current(msgAnalyticsStep(step));  // GA4 forwarder shape
    if (step === 2 && !step2SeenRef.current) {
      step2SeenRef.current = true; // InitiateCheckout once per session, not per back-and-forth
      try { window.fbq?.("track", "InitiateCheckout"); } catch { /* pixel blocked */ }
      try { window.gtag?.("event", "begin_checkout"); } catch { /* tag blocked */ }
    }
  }, [step]);

  // ── Embed: theme to the operator's accent + auto-resize the host iframe ──
  useEffect(() => {
    if (!IS_EMBED) return;
    if (EMBED_ACCENT && !document.getElementById("scoop-embed-accent")) {
      const a = document.createElement("style");
      a.id = "scoop-embed-accent";
      a.textContent = `
        .scoop-cta{background:${EMBED_ACCENT} !important;box-shadow:0 10px 26px -10px ${EMBED_ACCENT}99 !important}
        .scoop-progress{background:${EMBED_ACCENT} !important}
        .scoop-input:focus{border-color:${EMBED_ACCENT} !important;box-shadow:0 0 0 4px ${EMBED_ACCENT}2b !important}
        .scoop-suggestion:hover{border-left-color:${EMBED_ACCENT} !important}
        .scoop-disclosure,.scoop-readd{color:${EMBED_ACCENT} !important}
        .scoop-pricebox{background:${EMBED_ACCENT}0d !important;border-color:${EMBED_ACCENT}36 !important;box-shadow:0 8px 22px -14px ${EMBED_ACCENT}73 !important}
        .scoop-price-eyebrow{color:${EMBED_ACCENT} !important}
      `;
      document.head.appendChild(a);
    }
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    // Post our content height so the parent iframe can resize to fit (no inner scrollbar).
    // TWO shapes: the legacy one the existing Settings embed snippet listens for, and
    // the ads-handoff contract shape ({source:"iscoopcrm", type:"resize"}).
    let lastH = 0;
    const post = () => {
      try {
        const h = document.documentElement.scrollHeight;
        if (h === lastH) return; // the 1s interval is a safety net, not a firehose
        lastH = h;
        window.parent.postMessage({ type: "scoop-signup-height", height: h }, "*");
        emitRef.current(msgResize(h));
      } catch { /* not framed */ }
    };
    post();
    const ro = new ResizeObserver(post); ro.observe(document.documentElement);
    const t = setInterval(post, 1000); // belt-and-suspenders for late layout (Stripe iframe, fonts)
    window.addEventListener("load", post);
    return () => { ro.disconnect(); clearInterval(t); window.removeEventListener("load", post); };
  }, []);

  // ── Clamp deo cadence to the DEO<=CLEANUP rule ────────────────────
  // If the user changes cleanup frequency so the current deo_frequency is no
  // longer allowed (e.g. weekly deo, then switches to biweekly cleanup), reset
  // it to the most-frequent still-allowed option. Never lets an invalid
  // deo>cleanup combo persist in state (and therefore never submit one).
  useEffect(() => {
    if (!form.frequency) return;
    const allowed = DEO_FREQ_OPTIONS[form.frequency] || DEO_FREQ_ORDER;
    if (!allowed.includes(form.deo_frequency)) {
      const next = DEO_FREQ_ORDER.find(f => allowed.includes(f)) || "biweekly";
      setForm(p => ({ ...p, deo_frequency: next }));
    }
  }, [form.frequency, form.deo_frequency]);

  // ── Never leave a doomed cadence selected ─────────────────────────
  // Twice-weekly is only schedulable in zips that have a SECOND service day.
  // If the customer picks it and then edits their address to a zip without one,
  // fall back to the default cadence rather than letting the signup blow up at
  // the moment they submit their card.
  useEffect(() => {
    if (!cfg || form.frequency !== "twice-weekly") return;
    if (cfg.service_area?.find(z => z.zip === form.zip)?.second_day) return;
    const freqs = [...new Set((cfg.prices || []).filter(p => p.service === "cleanup").map(p => p.frequency))];
    const next  = freqs.includes("weekly") ? "weekly" : (freqs.find(f => f !== "twice-weekly") || "weekly");
    setForm(p => ({ ...p, frequency: next }));
  }, [cfg, form.zip, form.frequency]);

  // ── Address autocomplete (Places API New, REST) — UNCHANGED ───────
  useEffect(() => {
    if (justPicked.current) { justPicked.current = false; setSuggestions([]); return; }
    const q = (form.address || "").trim();
    // No key OR too short to query: clear suggestions. If the lookup is simply
    // unavailable (no Maps key), reveal the manual fallback so the user is never
    // dead-ended on the address step.
    if (q.length < 4 || !GOOGLE_MAPS_KEY) {
      setSuggestions([]);
      if (!GOOGLE_MAPS_KEY) setAddressManual(true);
      return;
    }
    if (!acSession.current) acSession.current = (window.crypto?.randomUUID?.() || String(Math.random()).slice(2));
    const t = setTimeout(async () => {
      try {
        const r = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Goog-Api-Key": GOOGLE_MAPS_KEY },
          body: JSON.stringify({ input: q, includedRegionCodes: ["us"], sessionToken: acSession.current }),
        });
        const d = await r.json();
        const list = Array.isArray(d.suggestions) ? d.suggestions.map(s => s.placePrediction).filter(Boolean).slice(0, 5) : [];
        setSuggestions(list);
        // Track whether THIS query produced any hits. When a non-trivial query
        // returns nothing (key issue / unrecognized address), reveal the manual
        // fallback so they can proceed by typing the zip themselves.
        hadSuggestions.current = list.length > 0;
        if (list.length === 0) setAddressManual(true);
      } catch { setSuggestions([]); setAddressManual(true); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.address]);

  async function pickSuggestion(p) {
    try {
      const r = await fetch(`https://places.googleapis.com/v1/places/${p.placeId}?sessionToken=${acSession.current || ""}`, {
        headers: { "X-Goog-Api-Key": GOOGLE_MAPS_KEY, "X-Goog-FieldMask": "addressComponents,location,formattedAddress" },
      });
      const place = await r.json();
      const C = {}; (place.addressComponents || []).forEach(c => (c.types || []).forEach(t => { C[t] = c; }));
      const g = (k, w) => C[k] ? (w === "short" ? C[k].shortText : C[k].longText) : "";
      const street = [g("street_number"), g("route")].filter(Boolean).join(" ");
      justPicked.current = true; acSession.current = null;
      setForm(pp => ({ ...pp,
        address: street || place.formattedAddress || pp.address,
        city: g("locality") || g("sublocality") || g("postal_town") || pp.city,
        state: g("administrative_area_level_1", "short") || pp.state,
        zip: g("postal_code") || pp.zip,
        lat: place.location?.latitude ?? null, lng: place.location?.longitude ?? null,
      }));
      setSuggestions([]);
      hadSuggestions.current = false;
      setAddressConfirmed(true); // positive confirmation on the address field
      setAddrHint("");           // a pick clears the "choose from list" gate hint
    } catch { setSuggestions([]); }
  }

  // ── Mount/destroy Stripe card element (mirrors ClientModal) ───────
  useEffect(() => {
    // Only on step 3, when the card is the chosen path and the account exists.
    if (step !== 3 || !wantCard || mountedRef.current) return;
    // Per-run cancelled flag guards against a fast now→later→now toggle racing
    // two async loadStripe() resolutions into a double mount.
    let cancelled = false;
    loadStripe(cfg.stripe_account_id).then(stripe => {
      if (cancelled) return;
      stripeRef.current   = stripe;
      elementsRef.current = stripe.elements();
      const card = elementsRef.current.create("card", {
        hidePostalCode: true,
        style: {
          // 16px for the same reason as S.input — under 16 iOS zooms when the card
          // field (an iframe input) is focused, right at the payment moment.
          base: { fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", fontSize: "16px", color: "#0f172a", "::placeholder": { color: "#94a3b8" } },
          invalid: { color: "#b91c1c" },
        },
      });
      card.mount("#scoop-card-element");
      card.on("change", e => { setCardError(e.error?.message || ""); setCardComplete(e.complete); });
      card.on("focus", () => {
        if (paymentStartedRef.current) return;
        paymentStartedRef.current = true; // once per session — GA4 wants the moment, not every refocus
        emitRef.current(msgAction("payment_started"));
        // Persist it on the lead (non-blocking): pushes this lead's GHL quiet
        // period out to 10 minutes AFTER the card touch, and marks them the
        // hottest abandonment stage. Phone is the dedup key — validated by step 1.
        try {
          fetch(`${SUPABASE_URL}/functions/v1/public-capture-lead`, {
            method: "POST",
            headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
            body: JSON.stringify({ slug, kind: "in_area", phone: formRef.current.phone, zip: formRef.current.zip, payment_started: true }),
          }).catch(() => {});
        } catch { /* never let telemetry touch checkout */ }
      });
      cardElRef.current = card;
      mountedRef.current = true; // only mark mounted after a successful mount
      setStripeReady(true);
    }).catch(() => { if (!cancelled) setCardError("Couldn't load the secure card form. You can still sign up and add your card later."); });
    return () => {
      cancelled = true;
      if (cardElRef.current) { cardElRef.current.destroy(); cardElRef.current = null; }
      mountedRef.current = false;
    };
  }, [step, wantCard]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cloudflare Turnstile: load script once + render widget on step 3 ──
  // Env-driven & graceful: with no site key this is a complete no-op.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || step !== 3) return;
    let cancelled = false;

    // Inject the Turnstile API script exactly once.
    if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    // Poll briefly for window.turnstile, then explicit-render once.
    const tryRender = () => {
      if (cancelled) return;
      const el = turnstileMountRef.current;
      if (!el || turnstileWidgetId.current != null) return; // already rendered
      if (!window.turnstile || typeof window.turnstile.render !== "function") return;
      try {
        turnstileWidgetId.current = window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token || ""),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
        });
      } catch { /* don't crash the form if Turnstile isn't ready */ }
    };

    tryRender();
    const poll = setInterval(() => {
      if (cancelled || turnstileWidgetId.current != null) { clearInterval(poll); return; }
      tryRender();
    }, 300);

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (turnstileWidgetId.current != null && window.turnstile?.remove) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
      }
      turnstileWidgetId.current = null;
    };
  }, [step]);

  // A Turnstile token is SINGLE-USE. After a failed submit the token is already
  // spent, so resending it always fails — leaving the customer stuck with no way
  // to retry. Reset the widget to issue a FRESH challenge + clear the stale token.
  function resetTurnstile() {
    setTurnstileToken("");
    try {
      if (turnstileWidgetId.current != null && window.turnstile?.reset) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } catch { /* widget not ready — next render issues a fresh token */ }
  }

  // Switch to the "I'll add later" path → tear down the live card element.
  function chooseLater() {
    if (cardElRef.current) { cardElRef.current.destroy(); cardElRef.current = null; }
    mountedRef.current = false;
    setStripeReady(false); setCardComplete(false); setCardError("");
    setCardChoice("later");
  }
  function chooseNow() { setCardChoice("now"); }

  // ── Validation helpers ────────────────────────────────────────────
  const day   = serviceDay(form.zip);
  const price = priceFor(form.frequency, form.dogs);
  const pricePerVisit = perVisitFor(form.frequency, form.dogs);
  // Bold per-visit for the summary box/CTA. The deo per-visit joins it only when
  // both services share a cadence — then every visit genuinely costs the sum. On
  // mixed cadences a combined "per visit" would be a made-up number, so the plan's
  // own per-visit anchors and the monthly parenthetical carries the deo add-on.
  const summaryPerVisit = pricePerVisit == null ? null
    : (form.deo_enabled && form.deo_frequency === form.frequency && deoPriceFor(form.frequency) != null
        ? Math.round((pricePerVisit + Number(deoPriceFor(form.frequency))) * 100) / 100
        : pricePerVisit);

  const phoneDigits = form.phone.replace(/\D/g, "");
  const valid = {
    first_name: form.first_name.trim().length > 0,
    last_name:  form.last_name.trim().length > 0,
    email:      EMAIL_RE.test(form.email.trim()),
    phone:      phoneDigits.length === 10,
    zip:        /^\d{5}$/.test(form.zip),
  };
  // Phone is now collected on step 1 (required for the in-area lead). Surface a
  // SOFT problem on blur when partially filled.
  const phoneSoftError = touched.phone && phoneDigits.length > 0 && phoneDigits.length !== 10;
  // Generic valid/invalid border for required fields — but NOT phone (soft-only).
  const fieldClass = (k) => "scoop-input" + (touched[k] && form[k] ? (valid[k] ? " valid" : " invalid") : "");
  const phoneClass = () => "scoop-input" + (phoneSoftError ? " soft-invalid" : (touched.phone && valid.phone && phoneDigits.length === 10 ? " valid" : ""));
  const markTouched = (k) => setTouched(t => ({ ...t, [k]: true }));

  // Is the typed zip a valid 5-digit zip inside the operator's service area?
  const inArea = valid.zip && !!serviceDay(form.zip);
  // Step-1 gate: a valid in-area zip, an address, AND a valid phone must all be
  // present to advance (phone + address/zip are captured before the plan step).
  const step1Ready = inArea && !!form.address.trim() && valid.phone;

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Save the in-area lead (non-blocking) ──────────────────────────
  // Fires at the transition into the plan/price step. Guarded by leadSaved so
  // it runs at most once per session; the backend dedups so a re-save after
  // edits is harmless. A failure here must NEVER block the customer.
  async function captureInAreaLead() {
    if (leadSaved.current) return;
    leadSaved.current = true;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/public-capture-lead`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({
          slug, kind: "in_area",
          event_id: EVENT_ID,
          ...(ATTRIBUTION ? { attribution: ATTRIBUTION } : {}),
          furthest_step: "plan", // address done — they are now on the plan step
          phone: form.phone, address: form.address,
          city: form.city, state: form.state, zip: form.zip,
          lat: form.lat, lng: form.lng,
        }),
      }).then(r => r.json());
      if (res && res.lead_id) setLeadId(res.lead_id);
    } catch { /* non-blocking: capture failure must not stop signup */ }
  }

  // ── Save the PLAN onto the lead at price-reveal (non-blocking) ────
  // Fires when the customer taps "See my price" (and again on Continue) so the
  // lead created at step 1 gets PATCHed with dogs + frequency + deodorizer + the
  // quoted price. public-capture-lead dedups by phone, so it updates the SAME
  // row — a bailer who only reveals their price still leaves us their full intent.
  async function capturePartialLead(furthestStep = "plan") {
    if (!valid.phone) return; // phone is the dedup key — without it we'd orphan a row
    const monthly = price == null ? null
      : (form.deo_enabled && deoDelta != null ? Number(price) + Number(deoDelta) : Number(price));
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/public-capture-lead`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({
          slug, kind: "in_area",
          event_id: EVENT_ID,
          ...(ATTRIBUTION ? { attribution: ATTRIBUTION } : {}),
          furthest_step: furthestStep,
          phone: form.phone, zip: form.zip,
          dogs: Number(form.dogs), frequency: form.frequency,
          deo_enabled: form.deo_enabled, deo_frequency: form.deo_frequency,
          // quoted_per_visit is the PER-VISIT price (it carried the monthly amount
          // until 2026-08-19, which mislabeled the admin lead card).
          quoted_per_visit: pricePerVisit ?? null, quoted_monthly: monthly,
        }),
      });
    } catch { /* non-blocking: a capture hiccup must never stop the signup */ }
  }

  // Advance step 1 → 2: enforce the address-pick gate, then save the lead.
  function goToPlan() {
    // ADDRESS ENFORCEMENT: if suggestions are currently showing, the user must
    // PICK one (which sets lat/lng + addressConfirmed). Block until they do.
    if (suggestions.length > 0 && !addressConfirmed) {
      setAddrHint("Please choose your address from the list.");
      track("validation_error", "address");
      return;
    }
    if (!step1Ready) {
      track("validation_error", !form.address || !addressConfirmed ? "address" : !valid.zip ? "zip" : "phone");
      return;
    }
    setAddrHint("");
    track("continue_clicked", "address");
    captureInAreaLead(); // fire-and-forget; non-blocking
    setStep(2);
  }

  // Backend errors are written for the OPERATOR ("Set a second day for 08012 in
  // Settings → Team & Service → Service Area"). Showing that raw text to a
  // paying stranger is both confusing and a leak of internal setup. Map to copy
  // the customer can actually act on; the raw text still goes to the console.
  function customerSafeError(raw) {
    const t = String(raw || "");
    if (/second\s*service\s*day|second_day/i.test(t))
      return "That schedule isn't available at your address yet. Please choose another frequency — or get in touch and we'll set it up for you.";
    if (/zip/i.test(t) && /service|area|cover/i.test(t))
      return `Sorry — we don't currently service zip ${form.zip}.`;
    if (/card|payment method|declin|insufficient/i.test(t))
      return "We couldn't process that card. Please double-check the details or try a different card.";
    if (/already|duplicate|exist/i.test(t))
      return "It looks like there's already an account with that email. Please get in touch and we'll pick up from there.";
    return "We couldn't finish your signup just now. Please try again — or get in touch and we'll complete it for you.";
  }

  // ── Submit (preserves required-field, service-day, geocode, POST) ──
  async function submit(e) {
    if (e) e.preventDefault();
    // Enter-key in a step-1/step-2 input must NOT trigger the final submit:
    // only step 3 owns the actual validation + POST.
    if (step !== 3) return;
    setErr("");

    // If on the card path and the card isn't complete, don't run final
    // validation — guide them to finish (or skip) the card instead.
    if (wantCard && !cardComplete) {
      setCardError("");
      cardElRef.current?.focus?.();
      document.getElementById("scoop-card-element")?.scrollIntoView({ behavior: "smooth", block: "center" });
      setErr("Finish your card details, or tap “I'll add it later” below.");
      track("validation_error", "card");
      return;
    }

    // Bot check: only enforced when a Turnstile site key is configured.
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErr("Please complete the “I'm human” check.");
      track("validation_error", "turnstile");
      return;
    }

    // Per-field required check → focus the first invalid field.
    // Fields that live on step 1 (zip/phone) bounce the user back to step 1.
    const order = ["first_name", "last_name", "email", "phone", "zip"];
    for (const k of order) {
      if (!valid[k] || !form[k]) {
        setTouched(t => ({ ...t, [k]: true }));
        if (k === "zip" || k === "phone") setStep(1);
        // Focus after the step switch so the right field is mounted.
        setTimeout(() => document.getElementById(`scoop-${k}`)?.focus(), 0);
        setErr(k === "email" ? "That email looks off — double-check it so we can reach you."
             : k === "zip"   ? "Enter a 5-digit zip so we can check your area."
             : k === "phone" ? "Add a 10-digit phone so we can send visit reminders."
             : "Please fill in your name and email.");
        track("validation_error", k);
        return;
      }
    }
    // Address is enforced at the step-1 gate; kept here as a defensive no-op
    // bounce in case anyone reaches step 3 without it.
    if (!form.address) { setErr("Please add your service address."); setStep(1); return; }
    if (!serviceDay(form.zip)) { setErr(`Sorry — we don't currently service zip ${form.zip}.`); setStep(1); return; }

    track("continue_clicked", "payment");
    setSubmitting(true);
    try {
      // Geocode fallback (unchanged) — Places usually set lat/lng already.
      let { lat, lng } = form;
      if ((lat == null || lng == null) && form.address && form.zip) {
        try { const gc = await geocodeAddress(form.address, form.zip); if (gc) { lat = gc.lat; lng = gc.lng; } } catch {}
      }

      // Optional card: tokenize on the connected account if the customer chose it.
      let cardFields = {};
      if (wantCard && cardComplete) {
        const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
          type: "card", card: cardElRef.current,
          billing_details: { name: `${form.first_name} ${form.last_name}`.trim(), email: form.email },
        });
        if (error) { setCardError(error.message); setSubmitting(false); return; }
        cardFields = { payment_method_id: paymentMethod.id, pm_last4: paymentMethod.card?.last4, pm_brand: paymentMethod.card?.brand };
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/public-onboard-client`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({
          slug, ...form, dogs: Number(form.dogs), lat, lng, ...cardFields,
          ...(leadId ? { lead_id: leadId } : {}),
          ...(TURNSTILE_SITE_KEY && turnstileToken ? { turnstile_token: turnstileToken } : {}),
          // Conversion plumbing: the browser's event id (dedups the server webhook
          // against the pixels) and any ad attribution from the landing URL/cookies.
          event_id: EVENT_ID,
          ...(ATTRIBUTION ? { attribution: ATTRIBUTION } : {}),
        }),
      }).then(r => r.json());
      if (res.error) {
        console.error("[signup] public-onboard-client failed:", res.error);
        // The duplicate-account message is written FOR the customer by the server (it names
        // the business and its phone number), so show it as-is rather than sending it through
        // customerSafeError, which exists to scrub operator-facing text.
        setErr(res.code === "duplicate_client" ? res.error : customerSafeError(res.error));
        resetTurnstile(); setSubmitting(false); return;
      }
      // Trust the server's verified subscription status, not merely the presence
      // of a submitted card. A declined card can exist while the client remains
      // payment_pending and must not be reported as a paid conversion.
      const activated = !!res.activated;
      setActivatedNow(activated);
      setDone(true);

      // ── Funnel outcome ───────────────────────────────────────────────
      const monthlyValue = Math.round(((price || 0) + (form.deo_enabled ? (deoDelta || 0) : 0)) * 100) / 100;

      // Enhanced Conversions can only be assembled where the customer's details are —
      // in here. Computed BEFORE the emit so the hashes can ride along to the parent,
      // which otherwise has no way to enhance the conversion it fires.
      let userData = null;
      try {
        const [sha_email, sha_phone] = await Promise.all([
          sha256Hex(normalizeEmail(form.email)),
          sha256Hex(normalizePhoneE164(form.phone)),
        ]);
        const u = {};
        if (sha_email) u.sha256_email_address = sha_email;
        if (sha_phone) u.sha256_phone_number  = sha_phone;
        if (Object.keys(u).length) userData = u;
      } catch { /* no crypto.subtle — send the conversion unenhanced rather than not at all */ }
      // Hashes go to a NAMED origin only. Under the "*" fallback any page that framed
      // us would receive them, and a hashed email is still an identifier.
      const namedOrigin = parseAllowedOrigins(cfg?.tracking?.embed_allowed_origins).length > 0;

      if (activated) {
        emitRef.current(msgSignupComplete({
          event_id: EVENT_ID, frequency: form.frequency, dogs: form.dogs,
          monthly_value: monthlyValue, card_on_file: true,
          ...(namedOrigin && userData ? { user_data: userData } : {}),
        }));
        try { window.fbq?.("track", "Subscribe", { value: monthlyValue, currency: "USD" }, { eventID: EVENT_ID }); } catch { /* pixel blocked */ }
      } else {
        emitRef.current(msgSignupPending({
          event_id: EVENT_ID, frequency: form.frequency, dogs: form.dogs,
          monthly_value: monthlyValue,
        }));
        try { window.fbq?.("track", "Lead", { value: monthlyValue, currency: "USD" }, { eventID: EVENT_ID }); } catch { /* pixel blocked */ }
        try { window.gtag?.("event", "generate_lead", { value: monthlyValue, currency: "USD" }); } catch { /* tag blocked */ }
      }

      // Enhanced Conversions for the FULL-PAGE path: hashed email/phone let Google
      // match this signup to the original ad click when cookies were blocked. Must be
      // set before the conversion event. No-ops when embedded (no gtag in the frame).
      try { if (userData) window.gtag?.("set", "user_data", userData); } catch { /* tag blocked */ }

      // Only a server-verified active subscription is a purchase / Ads bidding
      // conversion. A pending-card signup above remains visible as a lead without
      // teaching Google to buy non-paying registrations.
      const gt = cfg?.tracking || {};
      if (activated) {
        try { window.gtag?.("event", "purchase", { value: monthlyValue, currency: "USD", transaction_id: EVENT_ID }); } catch { /* tag blocked */ }
        try {
          if (gt.google_ads_conversion_id && gt.google_ads_conversion_label) {
            window.gtag?.("event", "conversion", {
              send_to: `${gt.google_ads_conversion_id}/${gt.google_ads_conversion_label}`,
              value: monthlyValue, currency: "USD", transaction_id: EVENT_ID,
            });
          }
        } catch { /* tag blocked */ }
      }

      // Full-page sessions bounce to the operator's thank-you page (never inside an
      // iframe — that would navigate only the frame; embeds use the postMessage above).
      // The 400ms grace lets the pixel beacons flush before navigation cancels them.
      const redirect = activated && !IS_FRAMED && cfg?.tracking?.thankyou_redirect_url
        ? buildThankYouUrl(cfg.tracking.thankyou_redirect_url, {
            event_id: EVENT_ID, plan: planSlug(form.frequency),
            dogs: Number(form.dogs), value: monthlyValue,
          })
        : null;
      if (redirect) setTimeout(() => { try { window.location.assign(redirect); } catch { /* stay on success screen */ } }, 400);
    } catch { setErr("Something went wrong. Please try again."); resetTurnstile(); setSubmitting(false); }
  }

  // Waitlist (out-of-area) — saves an out_of_area lead so the operator can
  // follow up when they expand. Optimistic: show the thank-you immediately and
  // POST in the background (a capture hiccup shouldn't punish the customer).
  async function submitWaitlist(e) {
    e.preventDefault();
    const email = waitlist.trim();
    if (!EMAIL_RE.test(email)) { setWaitlistErr("Please enter a valid email."); return; }
    setWaitlistErr("");
    setWaitlistDone(true);
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/public-capture-lead`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({
          slug, kind: "out_of_area",
          event_id: EVENT_ID,
          ...(ATTRIBUTION ? { attribution: ATTRIBUTION } : {}),
          email, zip: form.zip,
          ...(phoneDigits.length === 10 ? { phone: form.phone } : {}),
        }),
      });
    } catch { /* non-blocking: the customer still sees the thank-you */ }
  }

  // ── Render ─────────────────────────────────────────────────────────
  const S = styles;
  if (loadErr) return <Shell><div style={S.card}><h2 style={S.h2}>Not available</h2><p style={S.muted}>{loadErr}</p></div></Shell>;
  if (!cfg)    return <Shell><div style={S.card}><p style={S.muted}>Loading…</p></div></Shell>;
  if (!cfg.accepting) return <Shell><div style={S.card}><h2 style={S.h2}>{cfg.name}</h2><p style={S.muted}>This business isn't accepting online signups just yet. Please reach out to them directly.</p></div></Shell>;

  if (done) {
    return (
      <Shell>
        {/* Progress complete on the success screen */}
        <div style={S.progressTrack} role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100} aria-label="Signup complete">
          <div className="scoop-progress" style={{ ...S.progressFill, width: "100%" }} />
        </div>
        <div style={{ ...S.card, textAlign: "center" }}>
          <div style={S.successBadge} className="scoop-pop">{activatedNow ? "🐾" : "📬"}</div>
          {activatedNow ? (
            <>
              <h2 style={S.h2}>You're in{form.first_name ? `, ${form.first_name}` : ""}!</h2>
              <p style={S.muted} role="status" aria-live="polite">
                Your first scoop with <strong>{cfg.name}</strong> is scheduled{day ? <> for <strong style={{ textTransform: "capitalize" }}>{day}</strong></> : ""} — from here, we've got it handled. <strong>No charge today</strong>: billing only starts after your first visit, and you can cancel anytime. Confirmation and details are on their way to <strong>{form.email}</strong>.
              </p>
            </>
          ) : (
            <>
              <h2 style={S.h2}>Almost there{form.first_name ? `, ${form.first_name}` : ""} — one quick step</h2>
              <p style={S.muted} role="status" aria-live="polite">
                You're on the schedule with <strong>{cfg.name}</strong>{day ? <> for <strong style={{ textTransform: "capitalize" }}>{day}</strong></> : ""}. We just emailed <strong>{form.email}</strong> a secure link — add your card and your spot is locked in. Adding it starts your service, so you'll be billed for the visits left in this first month; your regular monthly plan begins on the 1st. Cancel anytime.
              </p>
            </>
          )}
        </div>
        <Footer />
      </Shell>
    );
  }

  // Rebalanced progress that actually advances and nearly fills by step 3.
  const pct = step === 1 ? 33 : step === 2 ? 66 : 90;

  return (
    <Shell>
      <div style={IS_EMBED ? { ...S.card, maxWidth: "none", boxShadow: "none", border: "none", padding: "2px 0", background: "transparent" } : S.card}>
        {/* ── Header: operator brand first, trust strip. In embed mode the operator's
            own site already shows their brand, so we drop the duplicate header. ── */}
        {IS_EMBED ? (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={S.trustStrip}>🔒 Stripe-secured · No charge today · Cancel anytime</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div style={S.eyebrow}>Dog Waste Removal</div>
            <h1 style={S.brand}>{cfg.name}</h1>
            <p style={S.muted}>Recurring poop-scooping, scheduled automatically. Set up in under a minute.</p>
            <div style={S.trustStrip}>🔒 Stripe-secured · Cancel anytime</div>
          </div>
        )}

        {/* ── Progress bar ── */}
        <div style={S.progressTrack} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Step ${step} of 3`}>
          <span style={S.srOnly}>Step {step} of 3</span>
          <div className="scoop-progress" style={{ ...S.progressFill, width: `${pct}%` }} />
        </div>

        <form onSubmit={submit} noValidate>
          {/* ════════ STEP 1 — Service area ════════ */}
          {step === 1 && (
            <div>
              <h2 style={S.stepH}>Let's check we cover your yard</h2>

              <label style={S.label} htmlFor="scoop-address">Service address</label>
              <div style={{ position: "relative", marginBottom: 4 }}>
                <input
                  id="scoop-address" className={"scoop-input" + (addressConfirmed && form.address ? " valid" : "")}
                  value={form.address} autoComplete="off"
                  onChange={e => { setField("address", e.target.value); setAddressConfirmed(false); }}
                  placeholder="Start typing your address…"
                  style={{ ...S.input, marginBottom: 0 }}
                />
                {suggestions.length > 0 && (
                  <div style={S.dropdown}>
                    {suggestions.map(p => (
                      <div key={p.placeId} className="scoop-suggestion" onMouseDown={() => pickSuggestion(p)} style={S.suggestion}>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.structuredFormat?.mainText?.text || p.text?.text}</div>
                        {p.structuredFormat?.secondaryText?.text && <div style={{ fontSize: 12, color: "#64748b" }}>{p.structuredFormat.secondaryText.text}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {addressConfirmed && form.address
                ? <div className="scoop-fade" style={S.okHint}>Address found ✓</div>
                : addrHint
                  ? <div className="scoop-fade" style={S.inlineErr} role="alert">{addrHint}</div>
                  : addressManual
                    ? <div style={S.privacy}>Keep typing your full street address…</div>
                    : <div style={S.privacy}>Start typing and pick your address from the list.</div>}

              <label style={S.label} htmlFor="scoop-zip">Zip</label>
              <input
                id="scoop-zip" className={fieldClass("zip")}
                value={form.zip} inputMode="numeric" maxLength={5}
                onChange={e => setField("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                onBlur={() => markTouched("zip")}
                placeholder="19720" style={S.input}
              />
              {touched.zip && form.zip && !valid.zip && (
                <div className="scoop-fade" style={S.inlineErr} role="alert">Enter a 5-digit zip so we can check your area.</div>
              )}

              {/* Phone — required here so phone + address + zip are all captured
                  BEFORE the plan/price step (and saved as the in-area lead). */}
              {inArea && (
                <>
                  <label style={S.label} htmlFor="scoop-phone">Phone</label>
                  <input id="scoop-phone" className={phoneClass()} style={S.input}
                    type="tel" inputMode="tel" autoComplete="tel" maxLength={12} value={form.phone}
                    onChange={e => setField("phone", formatPhoneInput(e.target.value))} onBlur={() => markTouched("phone")}
                    placeholder="555-555-5555" />
                  {phoneSoftError && <div className="scoop-fade" style={S.softHint} role="status" aria-live="polite">US numbers are 10 digits.</div>}
                  <p style={S.consentNote}>
                    By continuing you agree {cfg.name} may contact you about your quote by phone,
                    email, or text — including texts from our automated AI assistant. Msg &amp; data
                    rates may apply. Reply STOP to opt out.
                  </p>
                </>
              )}

              {/* Service-area result — a valid in-area zip (typed OR autocompleted)
                  always offers a path forward; advancing requires an address + phone. */}
              {inArea && (
                <>
                  <div className="scoop-slidein" style={S.okNote} role="status" aria-live="polite">
                    Nice — we scoop {form.zip} every <strong style={{ textTransform: "capitalize" }}>{day}</strong>. You're good to go. 🎉
                  </div>
                  {!form.address.trim() && (
                    <div className="scoop-fade" style={S.inlineErr} role="alert">Please enter your address to continue.</div>
                  )}
                  {/* The address got a hint but a missing PHONE didn't — so on mobile the
                      customer saw a purple button that silently ignored taps (0.6 opacity
                      on a gradient doesn't read as disabled). Same treatment, one hint at
                      a time: address first, then phone. */}
                  {!!form.address.trim() && !valid.phone && (
                    <div className="scoop-fade" style={S.inlineErr} role="alert">Add your 10-digit phone number to continue.</div>
                  )}
                  <button
                    type="button" className="scoop-cta"
                    style={{ ...S.btn, opacity: step1Ready ? 1 : 0.6, cursor: step1Ready ? "pointer" : "default" }}
                    disabled={!step1Ready}
                    onClick={goToPlan}
                  >
                    Yes, I'm in your area →
                  </button>
                </>
              )}

              {/* Out-of-area → soft waitlist (no dead button) */}
              {valid.zip && !day && (
                <div style={S.waitBox}>
                  {waitlistDone ? (
                    <p style={{ ...S.muted, margin: 0 }}>🎈 Thanks! We'll email you the day we expand to {form.zip}.</p>
                  ) : (
                    <>
                      <p style={{ ...S.muted, margin: "0 0 10px" }}>
                        We're not scooping {form.zip} just yet. Drop your email and we'll ping you the day we expand to your block.
                      </p>
                      <input
                        className="scoop-input" type="email" value={waitlist}
                        onChange={e => { setWaitlist(e.target.value); if (waitlistErr) setWaitlistErr(""); }}
                        placeholder="you@email.com" style={S.input}
                      />
                      {waitlistErr && <div className="scoop-fade" style={S.inlineErr} role="alert">{waitlistErr}</div>}
                      <button type="button" className="scoop-cta" style={{ ...S.btn, marginTop: 8, opacity: EMAIL_RE.test(waitlist.trim()) ? 1 : 0.6 }} onClick={submitWaitlist} disabled={!EMAIL_RE.test(waitlist.trim())}>
                        Notify me when you're here
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ════════ STEP 2 — Plan ════════ */}
          {step === 2 && (
            <div className="scoop-fade">
              <h2 style={S.stepH}>Build your plan</h2>

              <label style={S.label}>How many dogs?</label>
              <div style={S.segRow}>
                {[1, 2, 3, 4].map(n => {
                  const sel = Number(form.dogs) === n;
                  return (
                    <button
                      key={n} type="button"
                      className={"scoop-seg" + (sel ? " selected" : "")}
                      onClick={() => { setField("dogs", n); track("dogs_selected", n === 4 ? "4+" : String(n)); }}
                      style={{ ...S.seg, ...(sel ? (SEL_STYLE || S.segSel) : {}) }}
                    >{n}{n === 4 ? "+" : ""}</button>
                  );
                })}
              </div>

              <label style={S.label}>How often?</label>
              <div style={S.pillCol}>
                {cleanupFreqs.map(fr => {
                  const sel = form.frequency === fr;
                  return (
                    <button
                      key={fr} type="button"
                      className={"scoop-pill" + (sel ? " selected" : "")}
                      onClick={() => { setField("frequency", fr); track("frequency_selected", planSlug(fr)); }}
                      style={{ ...S.pill, ...(sel ? (SEL_STYLE || S.pillSel) : {}) }}
                    >
                      <span>{FREQ_LABELS[fr] || fr}</span>
                    </button>
                  );
                })}
              </div>

              {/* Deodorizer add-on toggle card */}
              <button
                type="button"
                className="scoop-addon"
                onClick={() => { const on = !form.deo_enabled; setField("deo_enabled", on); track("addon_toggled", on ? "deodorizer_on" : "deodorizer_off"); }}
                style={{ ...S.addon, ...(form.deo_enabled ? S.addonSel : {}) }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ ...S.addonCheck, ...(form.deo_enabled ? S.addonCheckSel : {}) }}>{form.deo_enabled ? "✓" : ""}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", textAlign: "left" }}>
                    💨 Add deodorizer treatment{priceRevealed && deoDelta != null && <span style={{ color: "#64748b", fontWeight: 500 }}>{"  ·  "}<PerVisitPrice pv={deoPriceFor(form.deo_frequency)} monthly={deoDelta} plus /></span>}
                  </span>
                </span>
              </button>

              {/* Deodorizer cadence — only when the add-on is on. Options are
                  filtered by the DEO<=CLEANUP rule (allowedDeoFreqs) so the
                  customer can never pick a deo cadence more frequent than their
                  cleanup cadence. Mirrors the cleanup pill aesthetic. */}
              {form.deo_enabled && (
                <div className="scoop-fade" style={S.deoFreqWrap} role="group" aria-label="Deodorizer frequency">
                  <label style={{ ...S.label, marginTop: 0 }}>How often should we deodorize?</label>
                  <div style={S.pillCol}>
                    {allowedDeoFreqs.map(fr => {
                      const sel = form.deo_frequency === fr;
                      return (
                        <button
                          key={fr} type="button"
                          className={"scoop-pill" + (sel ? " selected" : "")}
                          aria-pressed={sel}
                          onClick={() => { setField("deo_frequency", fr); track("deo_frequency_selected", planSlug(fr)); }}
                          style={{ ...S.pill, ...(sel ? (SEL_STYLE || S.pillSel) : {}) }}
                        >
                          <span>{FREQ_LABELS[fr] || fr}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Price reveal gate ──────────────────────────────────────
                  Nothing priced on this step until they pick a frequency and tap
                  "See my price". That tap reveals the price AND saves the plan
                  onto their lead (capturePartialLead), so a bailer still leaves
                  behind dogs + frequency + deo + the quote. */}
              {!priceRevealed ? (
                <>
                  <button
                    type="button" className="scoop-cta"
                    style={{ ...S.btn, opacity: form.frequency ? 1 : 0.6, cursor: form.frequency ? "pointer" : "default" }}
                    disabled={!form.frequency}
                    onClick={() => { setPriceRevealed(true); track("price_revealed"); capturePartialLead(); }}
                  >
                    See my price →
                  </button>
                  {!form.frequency && (
                    <p style={{ ...S.muted, fontSize: 11.5, textAlign: "center", marginTop: 8 }} role="status" aria-live="polite">
                      Pick how often we visit to see your price.
                    </p>
                  )}
                  <button type="button" style={S.backLink} onClick={() => { track("back_clicked", "plan"); setStep(1); }}>← Back</button>
                </>
              ) : (
                <div className="scoop-fade">
                  {/* THE price. One spot only (Luis: the per-row prices buried it) —
                      centered and celebratory, the reward for finishing the plan.
                      No plan recap here: their selections are right above. */}
                  {price != null && (
                    <div key={`${form.frequency}-${form.dogs}-${form.deo_enabled}-${form.deo_frequency}`} className="scoop-pricebox" style={S.priceBox}>
                      <div className="scoop-price-eyebrow" style={S.priceEyebrow}>Your price</div>
                      <div className="scoop-priceval" style={{ fontSize: 36, fontWeight: 800, color: "#0f172a", lineHeight: 1.05, letterSpacing: "-0.5px" }}>
                        {money(summaryPerVisit ?? price)}
                        <span style={{ fontSize: 16, fontWeight: 600, color: "#64748b" }}>{summaryPerVisit != null ? "/visit" : "/mo"}</span>
                      </div>
                      {summaryPerVisit != null && (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          ({money(Math.round(((price || 0) + (form.deo_enabled && deoDelta != null ? deoDelta : 0)) * 100) / 100)}/mo{form.deo_enabled ? ", deodorizer included" : ""})
                        </div>
                      )}
                    </div>
                  )}

                  {/* Service start date — drives the prorated first invoice on the
                      backend (create-customer prorates from start_date). Defaults to
                      today; future dates allowed up to the backend's 90-day cap. */}
                  <label style={S.label} htmlFor="scoop-start_date">Service start date</label>
                  <input
                    id="scoop-start_date" className="scoop-input" style={S.input}
                    type="date" value={form.start_date}
                    min={todayStr()} max={maxStartStr()}
                    onChange={e => { setField("start_date", e.target.value); track("date_changed"); }}
                  />
                  {form.start_date && form.start_date > todayStr() && (
                    <div className="scoop-fade" style={S.okHint} role="status" aria-live="polite">
                      ⏳ We'll save your card now — your first charge starts when service does.
                    </div>
                  )}

                  <button type="button" className="scoop-cta" style={{ ...S.btn, opacity: form.frequency ? 1 : 0.6 }} disabled={!form.frequency} onClick={() => { track("continue_clicked", "plan"); capturePartialLead("payment"); setStep(3); }}>
                    Looks good — continue →
                  </button>
                  <button type="button" style={S.backLink} onClick={() => { track("back_clicked", "plan"); setStep(1); }}>← Back</button>
                </div>
              )}
            </div>
          )}

          {/* ════════ STEP 3 — Info + card ════════ */}
          {step === 3 && (
            <div className="scoop-fade">
              <h2 style={S.stepH}>Your info & activation</h2>

              <div className="scoop-namerow" style={S.row}>
                <div style={S.col}>
                  <label style={S.label} htmlFor="scoop-first_name">First name</label>
                  <input id="scoop-first_name" className={fieldClass("first_name")} style={S.input}
                    autoComplete="given-name" value={form.first_name}
                    onChange={e => setField("first_name", e.target.value)} onBlur={() => markTouched("first_name")}
                    placeholder="Jane" />
                </div>
                <div style={S.col}>
                  <label style={S.label} htmlFor="scoop-last_name">Last name</label>
                  <input id="scoop-last_name" className={fieldClass("last_name")} style={S.input}
                    autoComplete="family-name" value={form.last_name}
                    onChange={e => setField("last_name", e.target.value)} onBlur={() => markTouched("last_name")}
                    placeholder="Smith" />
                </div>
              </div>

              <label style={S.label} htmlFor="scoop-email">Email</label>
              <input id="scoop-email" className={fieldClass("email")} style={S.input}
                type="email" inputMode="email" autoComplete="email" value={form.email}
                onChange={e => setField("email", e.target.value)} onBlur={() => markTouched("email")}
                placeholder="jane@email.com" />
              {touched.email && form.email && !valid.email
                ? <div className="scoop-fade" style={S.inlineErr} role="alert">That email looks off — double-check it so we can reach you.</div>
                : <div style={S.privacy}>For your receipt and service updates.</div>}

              {/* Phone is collected on step 1 — show a quiet, editable confirmation
                  here so the customer can fix it without backtracking. */}
              <label style={S.label} htmlFor="scoop-phone">Phone</label>
              <input id="scoop-phone" className={phoneClass()} style={S.input}
                type="tel" inputMode="tel" autoComplete="tel" maxLength={12} value={form.phone}
                onChange={e => setField("phone", formatPhoneInput(e.target.value))} onBlur={() => markTouched("phone")}
                placeholder="555-555-5555" />
              {phoneSoftError && <div className="scoop-fade" style={S.softHint} role="status" aria-live="polite">US numbers are 10 digits.</div>}

              {/* SMS opt-in — lets us text an ETA + a job-complete photo. Default
                  OFF (explicit opt-in — A2P reviewers prefer unchecked); STOP in
                  every text. Stored as the client's sms_consent. */}
              <button type="button" className="scoop-addon" onClick={() => setField("sms_consent", !form.sms_consent)}
                style={{ ...S.addon, marginTop: 10, ...(form.sms_consent ? S.addonSel : {}) }} aria-pressed={form.sms_consent}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ ...S.addonCheck, ...(form.sms_consent ? S.addonCheckSel : {}) }}>{form.sms_consent ? "✓" : ""}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: "#475569", textAlign: "left", lineHeight: 1.45 }}>
                    📱 Text me service updates from {cfg.name} — appointment confirmations, reminders &amp; my tech's ETA. Message frequency may vary; message &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent isn't required to buy; see the Privacy Policy &amp; Terms.
                  </span>
                </span>
              </button>

              {/* Notes disclosure */}
              {showNotes ? (
                <>
                  <label style={S.label} htmlFor="scoop-notes">Note for your scooper</label>
                  <textarea id="scoop-notes" className="scoop-input" style={{ ...S.input, minHeight: 70, resize: "vertical" }}
                    value={form.notes} onChange={e => setField("notes", e.target.value)}
                    placeholder="Gate code, dog names, backyard access…" />
                </>
              ) : (
                <button type="button" style={S.disclosure} onClick={() => setShowNotes(true)}>
                  + Add a note (gate code, dog names, backyard access)
                </button>
              )}

              {/* ════ CARD PROMOTION BLOCK ════ */}
              {canShowCard && wantCard && (
                <div className="scoop-cardblock" style={S.cardBlock}>
                  <div style={S.cardHead}>
                    <span style={S.cardTitle}>Activate today — add your card</span>
                    <span style={S.badge}>⚡ Instant activation</span>
                  </div>
                  <p style={S.cardBenefit}>
                    Add it now and your first scoop gets scheduled immediately. No charge today — billing starts once your service does. Cancel anytime.
                  </p>

                  <div style={S.lockRow}>
                    🔒 Your card is encrypted and sent straight to Stripe. {cfg.name} never sees or stores your full card number.
                  </div>

                  {!stripeReady && <div style={S.cardLoading}>Loading secure card form…</div>}
                  <div id="scoop-card-element" style={{ ...S.cardField, display: stripeReady ? "block" : "none", borderColor: cardError ? "#fecaca" : "#d7dbe6" }} />
                  {cardError
                    ? <div style={{ fontSize: 12.5, color: "#b91c1c", marginTop: 6 }}>⚠️ {cardError}</div>
                    : cardComplete && <div className="scoop-pop" style={{ fontSize: 12.5, color: "#166534", marginTop: 6, fontWeight: 600 }}>✓ Card looks good</div>}

                  <div style={S.noChargeLine}>
                    No charge today — billing starts once your service does. Cancel anytime, no contracts.
                  </div>
                </div>
              )}

              {/* Quiet secondary: skip / re-add */}
              {canShowCard && (
                wantCard
                  ? <button type="button" style={S.skipLink} onClick={chooseLater}>I'll add my card later — email me a secure link →</button>
                  : <button type="button" style={S.readdLink} onClick={chooseNow}>← Add card to activate instantly</button>
              )}

              {/* Risk-reversal chips */}
              <div style={S.chipRow}>
                <span style={S.chip}>✓ No contracts</span>
                <span style={S.chip}>✓ Cancel anytime</span>
              </div>

              {/* Billing recap — the CTA anchors on the per-visit price, but the
                  charge is monthly, and this is the step where it happens. The
                  monthly number must be on-screen at checkout or the first invoice
                  is a surprise (handoff v5: demoted to muted, never absent). */}
              {price != null && (
                <p style={{ ...S.muted, fontSize: 12, textAlign: "center", margin: "10px 0 0" }}>
                  {FREQ_LABELS[form.frequency] || form.frequency} · {form.dogs} dog{Number(form.dogs) > 1 ? "s" : ""}{form.deo_enabled ? " + deodorizer" : ""}
                  {summaryPerVisit != null ? ` — ${money(summaryPerVisit)}/visit` : ""}, billed monthly ({money(Math.round(((price || 0) + (form.deo_enabled && deoDelta != null ? deoDelta : 0)) * 100) / 100)}/mo)
                </p>
              )}

              {err && <div style={S.errBox} role="alert" aria-live="assertive">⚠️ {err}</div>}

              {/* Cloudflare Turnstile — only rendered when a site key is configured.
                  The widget renders into this div via the explicit-render effect. */}
              {TURNSTILE_SITE_KEY && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
                  <div ref={turnstileMountRef} />
                </div>
              )}

              {/* Primary CTA — NEVER disabled for an incomplete card; only for
                  in-flight submits (double-submit guard). On click, submit()
                  guides the user to finish or skip the card. */}
              {(() => {
                const label = submitting
                  ? "Activating your service…"
                  : wantCard
                    ? `Activate my service${summaryPerVisit != null ? ` · ${money(summaryPerVisit)}/visit` : price != null ? ` · ${money(deoDelta != null && form.deo_enabled ? Number(price) + Number(deoDelta) : price)}/mo` : ""} →`
                    : "Sign up — I'll add my card later →";
                return (
                  <>
                    <button type="submit" className="scoop-cta" disabled={submitting} style={{ ...S.btn, opacity: submitting ? 0.6 : 1, cursor: submitting ? "default" : "pointer" }}>
                      {submitting && <span className="scoop-spinner" />}{label}
                    </button>
                    {wantCard && !cardComplete && !submitting && (
                      <p style={{ ...S.muted, fontSize: 11.5, textAlign: "center", marginTop: 8 }} role="status" aria-live="polite">Add your card above to activate instantly — or tap “I'll add it later”.</p>
                    )}
                  </>
                );
              })()}
              <button type="button" style={S.backLink} onClick={() => { track("back_clicked", "payment"); setStep(2); }}>← Back</button>
            </div>
          )}
        </form>
      </div>
      <Footer />
    </Shell>
  );
}

function Footer() {
  return <div style={{ textAlign: "center", marginTop: 14, fontSize: 11, color: "#94a3b8" }}>🔒 Payments secured by Stripe · Powered by iScoopCRM</div>;
}

function Shell({ children }) {
  // Vertically centered on desktop (tall viewport, short card = balanced hero). On a
  // phone the same centering parked ~20% of dead background ABOVE the card before any
  // content appeared. The scoop-shell media query pins it to the top on small screens.
  return (
    <div className="scoop-shell" style={{ minHeight: IS_EMBED ? "auto" : "100vh", background: IS_EMBED ? "transparent" : "linear-gradient(160deg,#f1f5ff,#faf5ff)", display: "flex", flexDirection: "column", alignItems: IS_EMBED ? "stretch" : "center", justifyContent: IS_EMBED ? "flex-start" : "center", padding: IS_EMBED ? 0 : 18, fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif" }}>
      {children}
    </div>
  );
}

const styles = {
  card: { width: "100%", maxWidth: 460, background: "#fff", borderRadius: 20, padding: "26px 24px", boxShadow: "0 20px 60px -20px rgba(40,40,90,.25)", border: "1px solid #eef0f6" },
  h2: { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" },
  stepH: { fontSize: 18, fontWeight: 800, color: "#0f172a", margin: "4px 0 14px" },
  muted: { color: "#64748b", fontSize: 13.5, lineHeight: 1.55, margin: 0 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#8b7bff" },
  brand: { fontSize: 26, fontWeight: 800, margin: "6px 0 4px", color: "#0f172a" },
  trustStrip: { display: "inline-block", marginTop: 10, fontSize: 11.5, fontWeight: 600, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 999, padding: "4px 12px" },

  progressTrack: { height: 6, background: "#eef0f6", borderRadius: 999, overflow: "hidden", margin: "0 0 18px" },
  progressFill: { height: "100%", background: "linear-gradient(90deg,#6366f1,#8b5cf6)", borderRadius: 999 },

  row: { display: "flex", gap: 10, flexWrap: "wrap" },
  col: { flex: "1 1 150px", minWidth: 0 },
  label: { display: "block", fontSize: 12.5, fontWeight: 600, color: "#334155", margin: "10px 2px 5px" },
  // fontSize MUST be ≥16: iOS Safari auto-zooms the whole page when focusing any input
  // under 16px, and never zooms back out — the form stays horizontally clipped for the
  // rest of the signup (verified on the iPhone simulator 2026-08-10).
  input: { width: "100%", boxSizing: "border-box", padding: "11px 13px", fontSize: 16, border: "1px solid #d7dbe6", borderRadius: 11, marginBottom: 0, background: "#fff", color: "#0f172a" },

  dropdown: { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "#fff", border: "1px solid #e3e8ef", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,.12)", marginTop: 3, overflow: "hidden" },
  suggestion: { padding: "10px 13px", cursor: "pointer", fontSize: 13.5, borderBottom: "1px solid #f4f5f7", borderLeft: "3px solid transparent" },

  okNote: { fontSize: 13, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "11px 14px", margin: "14px 0 6px", lineHeight: 1.5 },
  inlineErr: { fontSize: 12, color: "#b91c1c", margin: "5px 2px 0" },
  softHint: { fontSize: 11.5, color: "#64748b", margin: "5px 2px 0", lineHeight: 1.4 },
  okHint: { fontSize: 11.5, color: "#166534", fontWeight: 600, margin: "5px 2px 0" },
  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 },
  waitBox: { marginTop: 14, padding: "14px 16px", background: "#f8fafc", border: "1px solid #eef0f6", borderRadius: 12 },

  segRow: { display: "flex", gap: 8 },
  seg: { flex: 1, padding: "12px 0", fontSize: 16, fontWeight: 700, color: "#475569", background: "#f8fafc", border: "1px solid #d7dbe6", borderRadius: 11, cursor: "pointer" },
  segSel: { background: "#6366f1", borderColor: "#6366f1", color: "#fff" },

  pillCol: { display: "flex", flexDirection: "column", gap: 8 },
  pill: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 15px", fontSize: 14, fontWeight: 600, color: "#334155", background: "#f8fafc", border: "1px solid #d7dbe6", borderRadius: 12, cursor: "pointer", textAlign: "left" },
  pillSel: { background: "#6366f1", borderColor: "#6366f1", color: "#fff" },

  addon: { width: "100%", marginTop: 12, padding: "13px 15px", background: "#f8fafc", border: "1px solid #d7dbe6", borderRadius: 12, cursor: "pointer", display: "flex" },
  addonSel: { background: "#f5f3ff", borderColor: "#c7d2fe" },
  addonCheck: { width: 20, height: 20, borderRadius: 6, border: "1.5px solid #cbd5e1", background: "#fff", color: "#fff", fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  addonCheckSel: { background: "#6366f1", borderColor: "#6366f1" },

  deoFreqWrap: { marginTop: 12, padding: "12px 14px", background: "#f5f3ff", border: "1px solid #c7d2fe", borderRadius: 12 },

  priceBox: { textAlign: "center", background: "linear-gradient(180deg,#f5f3ff,#fbfaff)", border: "1px solid #ddd6fe", borderRadius: 14, padding: "16px 18px 14px", margin: "16px 0 6px", boxShadow: "0 8px 22px -14px rgba(109,40,217,.45)" },
  priceEyebrow: { fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#7c3aed", marginBottom: 4 },
  consentNote: { fontSize: 10.5, color: "#94a3b8", lineHeight: 1.55, margin: "8px 2px 0" },

  cardBlock: { marginTop: 16, padding: "16px 16px 14px", background: "#f5f3ff", border: "1.5px solid #6366f1", borderRadius: 14, boxShadow: "0 12px 30px -16px rgba(99,102,241,.55)" },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  cardTitle: { fontSize: 14.5, fontWeight: 800, color: "#312e81" },
  badge: { fontSize: 11, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 999, padding: "3px 9px", whiteSpace: "nowrap" },
  cardBenefit: { fontSize: 12.5, color: "#4c4691", lineHeight: 1.5, margin: "8px 0 12px" },
  lockRow: { fontSize: 11.5, color: "#475569", background: "#fff", border: "1px solid #e0e7ff", borderRadius: 10, padding: "9px 12px", lineHeight: 1.45, marginBottom: 12 },
  cardLoading: { fontSize: 12.5, color: "#64748b", padding: "12px 13px", border: "1px solid #d7dbe6", borderRadius: 11, background: "#fff" },
  cardField: { padding: "13px 13px", border: "1px solid #d7dbe6", borderRadius: 11, background: "#fff" },
  noChargeLine: { fontSize: 12, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "9px 12px", marginTop: 12, lineHeight: 1.45, fontWeight: 500 },

  privacy: { fontSize: 11, color: "#94a3b8", margin: "5px 2px 0", lineHeight: 1.4 },
  disclosure: { display: "block", width: "100%", textAlign: "left", marginTop: 12, padding: 0, background: "none", border: "none", color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  skipLink: { display: "block", width: "100%", textAlign: "center", marginTop: 12, padding: "6px 0", background: "none", border: "none", color: "#64748b", fontSize: 12.5, fontWeight: 500, cursor: "pointer", textDecoration: "underline" },
  readdLink: { display: "block", width: "100%", textAlign: "center", marginTop: 12, padding: "6px 0", background: "none", border: "none", color: "#6366f1", fontSize: 13, fontWeight: 700, cursor: "pointer" },

  chipRow: { display: "flex", gap: 8, justifyContent: "center", marginTop: 14, flexWrap: "wrap" },
  chip: { fontSize: 11.5, fontWeight: 600, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 999, padding: "4px 11px" },

  errBox: { fontSize: 13, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", margin: "12px 0 0" },
  btn: { width: "100%", marginTop: 16, padding: "14px", border: "none", borderRadius: 12, cursor: "pointer", fontSize: 15.5, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", boxShadow: "0 10px 26px -10px rgba(99,102,241,.6)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  backLink: { display: "block", width: "100%", textAlign: "center", marginTop: 10, padding: "6px 0", background: "none", border: "none", color: "#94a3b8", fontSize: 12.5, fontWeight: 500, cursor: "pointer" },

  successBadge: { fontSize: 52, marginBottom: 8 },
};

// Injected once — handles :hover/:focus/.valid/keyframes that inline styles can't.
const CSS = `
.scoop-input { transition: border-color .15s ease, box-shadow .15s ease; outline: none; }
.scoop-input:hover { border-color: #b9c0d4; }
.scoop-input:focus { border-color: #6366f1; box-shadow: 0 0 0 4px rgba(99,102,241,.18); }
.scoop-input.valid { border-color: #22c55e; }
.scoop-input.invalid { border-color: #fca5a5; }
.scoop-input.soft-invalid { border-color: #fcd34d; }
.scoop-suggestion:hover { background: #f5f3ff; border-left-color: #8b5cf6 !important; }
.scoop-seg, .scoop-pill, .scoop-addon { transition: transform .08s ease, background .15s ease, border-color .15s ease, color .15s ease; }
.scoop-seg:hover, .scoop-pill:hover { border-color: #a5b4fc; }
.scoop-seg:active, .scoop-pill:active, .scoop-addon:active { transform: scale(.97); }
.scoop-cta { transition: transform .1s ease, box-shadow .15s ease, filter .15s ease; }
.scoop-cta:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.04); box-shadow: 0 14px 30px -10px rgba(99,102,241,.7); }
.scoop-cta:active:not(:disabled) { transform: translateY(1px) scale(.99); }
.scoop-spinner { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; display: inline-block; }
@media (prefers-reduced-motion: no-preference) {
  @keyframes scoopPop { 0% { transform: scale(.6); opacity: 0; } 60% { transform: scale(1.12); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes scoopSlideIn { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  @keyframes scoopFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes scoopPriceBump { 0% { background: #f5f3ff; } 100% { background: #f8fafc; } }
  @keyframes scoopSpin { to { transform: rotate(360deg); } }
  @keyframes scoopGrow { from { width: 0; } }
  .scoop-pop { animation: scoopPop .35s ease both; }
  .scoop-slidein { animation: scoopSlideIn .3s ease both; }
  .scoop-fade { animation: scoopFade .25s ease both; }
  .scoop-pricebox { animation: scoopPriceBump .5s ease; }
  .scoop-pricebox .scoop-priceval { animation: scoopPop .3s ease; display: inline-block; }
  .scoop-spinner { animation: scoopSpin .7s linear infinite; }
  .scoop-progress { transition: width .45s cubic-bezier(.22,.61,.36,1); }
  .scoop-cardblock { animation: scoopSlideIn .3s ease both; }
}
@media (max-width: 420px) {
  .scoop-namerow { flex-direction: column; }
  /* S.col is flex: 1 1 150px for the side-by-side desktop layout. Once the row goes
     column, that 150px BASIS becomes a 150px HEIGHT — each name field sat in a ~150px
     box and the form looked broken (~90pt of dead space per field on an iPhone).
     In column mode the fields must size to their content. !important because the
     basis arrives as an INLINE style (S.col), which plain CSS cannot override. */
  .scoop-namerow > div { flex: 0 0 auto !important; }
}
/* Phones: pin the card to the top. Desktop centers it (nice on a tall viewport), but on
   a phone centering parked ~20% of empty gradient above the fold before any content. */
@media (max-width: 640px) {
  .scoop-shell { justify-content: flex-start !important; padding-top: 10px !important; }
}
/* iOS renders type=date with an intrinsic width that ignores width:100% unless the
   native appearance is stripped — it stuck out past the card edge and got clipped. */
.scoop-input[type="date"] {
  -webkit-appearance: none;
  appearance: none;
  min-width: 0;
  display: block;
  text-align: left;
  min-height: 46px;
}
`;
